// Web push (Instellingen > Meldingen, NotificationSettings.js) — enkel voor de
// aanbieder (gebruiker), niet voor de klant. Gebruikt Firebase Cloud Messaging
// (FCM) puur als transportlaag: berichten komen binnen als een gewoon Push
// API-event, afgehandeld door de al bestaande `push`-listener in
// public/service-worker.js — er is bewust GEEN aparte firebase-messaging-sw.js
// nodig, dat zou een tweede, overlappende push-afhandeling in dezelfde service
// worker geven.
import { getMessaging, getToken, deleteToken, isSupported } from 'firebase/messaging';
import { doc, setDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { app, db, vapidKey } from '../firebase';

const STORAGE_KEY = 'flexitime_push_token_id';

// Simpele, niet-cryptografische hash — enkel om van een FCM-token (lang,
// onvoorspelbaar van vorm) een geldige/korte Firestore-document-id te maken.
// Geen beveiligingsdoel, puur een stabiele sleutel per token.
function hashToken(token) {
    let hash = 0;
    for (let i = 0; i < token.length; i++) {
        hash = (Math.imul(31, hash) + token.charCodeAt(i)) | 0;
    }
    return `t${(hash >>> 0).toString(36)}`;
}

export async function isPushSupported() {
    if (typeof window === 'undefined' || !('Notification' in window) || !('serviceWorker' in navigator)) {
        return false;
    }
    try {
        return await isSupported();
    } catch {
        return false;
    }
}

export function getStoredTokenId() {
    try {
        return localStorage.getItem(STORAGE_KEY);
    } catch {
        return null;
    }
}

// Vraagt de browserpermissie (toont het systeemvenstertje als die nog niet
// eerder beantwoord is) en slaat bij toestemming het FCM-token op voor dit
// toestel. Gooit 'unsupported'/'denied'/'no-token' bij respectievelijk geen
// ondersteuning, geweigerde permissie, of een onverwacht lege token.
export async function enablePushNotifications(uid) {
    if (!(await isPushSupported())) {
        throw new Error('unsupported');
    }
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
        throw new Error('denied');
    }

    const registration = await navigator.serviceWorker.ready;
    const messaging = getMessaging(app);
    const token = await getToken(messaging, { vapidKey, serviceWorkerRegistration: registration });
    if (!token) {
        throw new Error('no-token');
    }

    const tokenId = hashToken(token);

    // Firebase kan op hetzelfde toestel af en toe een NIEUW token uitgeven voor
    // dezelfde push-subscriptie (bv. na een service-worker-update) — zonder
    // opruiming zou het oude tokendocument geldig blijven staan en zou dit
    // toestel élke melding dubbel krijgen (1x per token). Vergelijk daarom met
    // het vorige, lokaal onthouden tokenId en verwijder dat document als het
    // een ander token bleek te zijn.
    const previousTokenId = getStoredTokenId();
    if (previousTokenId && previousTokenId !== tokenId) {
        try {
            await deleteDoc(doc(db, 'users', uid, 'pushTokens', previousTokenId));
        } catch (err) {
            console.error('Kon verouderd pushtoken niet opruimen:', err);
        }
    }

    await setDoc(doc(db, 'users', uid, 'pushTokens', tokenId), {
        token,
        userAgent: navigator.userAgent,
        createdAt: serverTimestamp(),
        lastSeenAt: serverTimestamp()
    });

    try {
        localStorage.setItem(STORAGE_KEY, tokenId);
    } catch {
        // Privénavigatie/uitgeschakelde opslag — het token blijft wel geregistreerd
        // op de backend, enkel de lokale "is dit toestel geregistreerd"-indicator ontbreekt.
    }

    return tokenId;
}

// Zet pushmeldingen weer uit voor dit toestel. Kan de browserpermissie zelf
// niet intrekken (dat kan enkel de gebruiker, via de browserinstellingen) —
// verwijdert wel het opgeslagen token, waardoor de backend er niets meer
// naartoe stuurt.
export async function disablePushNotifications(uid) {
    const tokenId = getStoredTokenId();
    if (tokenId) {
        try {
            await deleteDoc(doc(db, 'users', uid, 'pushTokens', tokenId));
        } catch (err) {
            console.error('Kon pushtoken niet verwijderen:', err);
        }
    }

    try {
        const messaging = getMessaging(app);
        await deleteToken(messaging);
    } catch {
        // Kan falen als er toch al geen actieve subscriptie meer was — geen probleem.
    }

    try {
        localStorage.removeItem(STORAGE_KEY);
    } catch {
        // Zie hierboven.
    }
}
